import styled from "styled-components";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { useState } from "react";
import { HashLink } from "react-router-hash-link";
import {
  MDBCardHeader,
  MDBCardBody,
  MDBCardTitle,
  MDBCard,
} from "mdb-react-ui-kit";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckSquare, faEdit } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { ActionButtonWrapper, Button, Row, Col } from "../App.styles";
import { Spinner } from "../common";
import { useAddMutation, useChallengeQuery } from "./Library.state";
import { pageState, pageSizeState } from "../organize/Organize.state";
import { useUserQuery } from "../monitor/User.state";
import LibraryChallengeNav from "./LibraryChallengeNav";
import ImageThumbnail from "../common/ImageThumbnail";

const ChallengeThumb = styled.div`
  background-color: ${({ theme }) => theme.placeholderColor};
  height: 75px;
  margin-right: 1rem;
  width: 75px;

  img {
    height: 75px;
    width: 75px;
    object-fit: cover;
    object-position: center;
  }

  ${({ error }) =>
    error &&
    `
    border: 2px solid red;

    img {
      display: none;
    }
  `};
`;

const Card = styled(MDBCard)`
  padding: 0;
  margin-bottom: 1rem;
`;

const CardHeader = styled(MDBCardHeader)`
  background-color: #37474f;
`;

const CardTitle = styled(MDBCardTitle)`
  color: white;
`;

const ImageCol = styled(Col)`
  align-items: flex-start;
`;

const Description = styled.div`
  max-width: 100%;

  iframe {
    max-width: 100%;
  }
`;

const LibraryChallenge = ({ id }) => {
  const { t } = useTranslation();
  const [added, setAdded] = useState(false);
  const useQuery = () => useChallengeQuery(id);
  const { assets, description, games, name, position } = useQuery();
  const pageSize = useRecoilValue(pageSizeState);
  const setPage = useSetRecoilState(pageState);
  const [error, setError] = useState(false);
  const { game } = useUserQuery();
  const { isLoading, mutateAsync } = useAddMutation(id);
  const libraryGame = games.find((game) => game.administrator);
  const canEdit = game.super_admin || !!libraryGame;
  const page = Math.floor(position / pageSize) + 1;

  const photo =
    // Thumbnails are served with ImageThumbnail component that will 
    // auto populate thumbnail URLs
    assets.photos.find((photo) => photo.name === "standard" && photo.url);

  const add = async () => {
    await mutateAsync();
    setAdded(true);
  };

  return (
    <Card>
      <CardHeader>
        <Row>
          <Col>
            <CardTitle>{name}</CardTitle>
          </Col>
        </Row>
      </CardHeader>
      <MDBCardBody>
        <Row>
          <ImageCol size="12" md="2">
            <ChallengeThumb error={error}>
              {photo?.url && (
                <ImageThumbnail
                  alt={name}
                  src={photo.url}
                  onError={() => setError(true)}
                />
              )}
            </ChallengeThumb>
          </ImageCol>
          <Col size="12" md="10">
            <Description dangerouslySetInnerHTML={{ __html: description }} />
          </Col>
        </Row>
        <LibraryChallengeNav id={id} />
        <ActionButtonWrapper>
          {canEdit && (
            <Button
              color="warning"
              className="btn-raised"
              onClick={() => setPage(page)}
            >
              <HashLink to={`/tasks/organize/${libraryGame.id}#${id}`}>
                <FontAwesomeIcon icon={faEdit} /> Edit
              </HashLink>
            </Button>
          )}
          {added ? (
            "Added."
          ) : (
            <Button color="primary" className="btn-raised" onClick={add}>
              <FontAwesomeIcon icon={faCheckSquare} /> {t('Add')}{" "}
              {isLoading && <Spinner size="0.875rem" />}
            </Button>
          )}
        </ActionButtonWrapper>
      </MDBCardBody>
    </Card>
  );
};

export default LibraryChallenge;
